import { Grid, IconButton } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { subYears } from 'date-fns';
import { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import Dropdown from '@acorn-portal/shared/src/components/dropdown';
import FormField from '@acorn-portal/shared/src/components/inputs/FormField';

interface IModal {
	title: string;
	description: string;
}

export interface IDropDownDates {
	day: string;
	month: string;
	year: string;
	minute: string;
	hour: string;
}

interface IProps {
	onChange: (values: IDropDownDates) => void;
	name?: string;
	id?: string;
	error?: boolean;
	helperText?: string | false;
	value?: unknown;
	label: string;
	modal?: IModal;
	month?: boolean;
	day?: boolean;
	year?: boolean;
	maxYear?: number;
	minYear?: number;
	time?: boolean;
	topLabel?: boolean;

	daySuffix?: boolean;
}

type DayFields = 'year' | 'month' | 'day';

const monthOptions = [
	{ label: 'January', value: '01' },
	{ label: 'February', value: '02' },
	{ label: 'March', value: '03' },
	{ label: 'April', value: '04' },
	{ label: 'May', value: '05' },
	{ label: 'June', value: '06' },
	{ label: 'July', value: '07' },
	{ label: 'August', value: '08' },
	{ label: 'September', value: '09' },
	{ label: 'October', value: '10' },
	{ label: 'November', value: '11' },
	{ label: 'December', value: '12' },
];

const getDaysInMonth = (month: number, year: number) => {
	return new Date(year, month + 1, 0).getDate();
};

const GetDays = (year: string, month: string) => {
	const options = [];

	const getMaxDays = () => {
		if (year && month) {
			return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
		}
		if (month) {
			return new Date(new Date().getFullYear(), parseInt(month, 10), 0).getDate();
		}
		return getDaysInMonth(Number(month), Number(year));
	};

	const maxDay = getMaxDays();

	for (let i = 1; i <= maxDay; i += 1) {
		const opt = i.toString();
		options.push({
			value: opt.length < 2 ? `0${opt}` : opt,
			label: opt,
		});
	}
	return options;
};

const GetYears = (maxYear: number, minYear: number) => {
	const options = [];
	for (let i = maxYear; i >= minYear; i -= 1) {
		if (i < minYear) break;
		options.push({
			label: i.toString(),
			value: i.toString(),
		});
	}
	return options;
};

function minTwoDigits(n: number) {
	return (n < 10 ? '0' : '') + n.toString();
}
const GetHours = (): Array<IOptions> => {
	const hours: Array<IOptions> = [];
	const maxHours = 23;
	for (let i = 0; i <= maxHours; i += 1) {
		hours.push({
			label: minTwoDigits(i),
			value: minTwoDigits(i),
		});
	}

	return hours;
};

const GetMinutes = (): Array<IOptions> => {
	const minutes: Array<IOptions> = [];
	const maxMinutes = 60;
	const interval = 15;

	for (let i = 0; i < maxMinutes; i += interval) {
		const string = minTwoDigits(i);

		minutes.push({
			label: string,
			value: string,
		});
	}

	return minutes;
};

const DatePickerField: FC<IProps> = ({
	error,
	helperText,
	id,
	label,
	modal,
	onChange,
	month = true,
	day = true,
	year = true,
	value,
	maxYear = new Date().getFullYear(),
	minYear = subYears(new Date(), 100).getFullYear(),
	time,
	topLabel,
	daySuffix = false,
}) => {
	const date = {
		...{
			day: '',
			month: '',
			year: '',
			hour: '',
			minute: '',
		},
		...(value as Partial<IDropDownDates>),
	};

	const [open, toggleModal] = useState(false);

	const daysOptions = useMemo(() => GetDays(date.year, date.month), [date.year, date.month]);

	const yearsOption = useMemo(() => GetYears(maxYear, minYear), [maxYear, minYear]);

	const hoursOptions = useMemo(() => GetHours(), []);

	const minutesOptions = useMemo(() => GetMinutes(), []);

	// Width configuration for different field combinations
	const getFieldCombinationKey = () => {
		const activeFields = [day && 'day', month && 'month', year && 'year'].filter(Boolean).join('-');
		return activeFields;
	};

	const getWidthForSingleField = () => 11;

	const getWidthForTwoFields = (field: DayFields) => {
		const widthMap = {
			year: modal ? 5 : 6,
			month: 6,
			day: 5,
		};
		return widthMap[field];
	};

	const getWidthForThreeFields = (field: DayFields) => {
		const widthMap = {
			year: modal ? 3 : 4,
			month: 5,
			day: 3,
		};
		return widthMap[field];
	};

	const getWidth = (field: 'year' | 'month' | 'day') => {
		const combination = getFieldCombinationKey();
		const fieldCount = combination.split('-').length;

		if (fieldCount === 1) {
			return getWidthForSingleField();
		}

		if (fieldCount === 2) {
			return getWidthForTwoFields(field);
		}

		if (fieldCount === 3) {
			return getWidthForThreeFields(field);
		}

		return 0;
	};

	return (
		<>
			<FormField error={error} helperText={helperText} id={id} label={label} topLabel={topLabel}>
				<Grid container direction='row' alignItems='center' spacing={1}>
					{day && (
						<Grid size={{ xs: getWidth('day'), md: getWidth('day') }}>
							<Dropdown
								error={error}
								data={daysOptions}
								id={`${id}.day`}
								name={`${id}.day`}
								label='Day'
								daySuffix={daySuffix}
								value={date.day}
								onChange={(x) => {
									onChange({
										day: x as string,
										month: date.month,
										year: date.year,
										hour: date.hour,
										minute: date.minute,
									});
								}}
							/>
						</Grid>
					)}
					{month && (
						<Grid
							size={{
								xs: getWidth('month'),
								md: getWidth('month'),
							}}
						>
							<Dropdown
								error={error}
								data={monthOptions}
								id={`${id}.month`}
								name={`${id}.month`}
								label='Month'
								value={date.month}
								onChange={(x) => {
									onChange({
										day: date.day,
										month: x as string,
										year: date.year,
										hour: date.hour,
										minute: date.minute,
									});
								}}
							/>
						</Grid>
					)}
					{year && (
						<Grid
							size={{
								xs: getWidth('year'),
								md: getWidth('year'),
							}}
						>
							<Dropdown
								error={error}
								data={yearsOption}
								id={`${id}.year`}
								name={`${id}.year`}
								label='Year'
								value={date.year}
								onChange={(x) => {
									onChange({
										day: date.day,
										month: date.month,
										year: x as string,
										hour: date.hour,
										minute: date.minute,
									});
								}}
							/>
						</Grid>
					)}
					{modal && (
						<Grid
							size={{ xs: 1 }}
							sx={(theme) => ({
								paddingLeft: '0.1rem !important',
								[theme.breakpoints.down('md')]: {
									paddingLeft: '0.2rem !important',
								},
							})}
						>
							<IconButton
								color='icon'
								aria-label={`${id ?? ''}-more-info`}
								sx={(theme) => ({
									my: 1,
									[theme.breakpoints.down('md')]: {
										px: 0,
									},
								})}
								onClick={() => toggleModal(true)}
							>
								<InfoIcon />
							</IconButton>
						</Grid>
					)}
					{time && (
						<>
							<Grid size={{ xs: 6, md: 6 }}>
								<Dropdown
									error={error}
									data={hoursOptions}
									id='hour'
									label='hour'
									name='hour'
									value={date.hour}
									onChange={(x) => {
										onChange({
											day: date.day,
											month: date.month,
											year: date.year,
											hour: x as string,
											minute: date.minute,
										});
									}}
								/>
							</Grid>

							<Grid size={{ xs: 6, md: 6 }}>
								<Dropdown
									error={error}
									data={minutesOptions}
									id='minute'
									label='minute'
									name='minute'
									value={date.minute}
									onChange={(x) => {
										onChange({
											day: date.day,
											month: date.month,
											year: date.year,
											hour: date.hour,
											minute: x as string,
										});
									}}
								/>
							</Grid>
						</>
					)}
				</Grid>
			</FormField>
			{modal && <HelperModal onClose={() => toggleModal(false)} open={open} {...modal} />}
		</>
	);
};

export default DatePickerField;
